import FailedIcon from "~/icons/FailedIcon";
import ShoppingCartIcon from "~/icons/ShoppingCartIcon";
import SoldOutIcon from "~/icons/SoldOutIcon";

export const PAY_IN_STORE_SCREEN = {
  NO_CHARGE_TITLE: "There is no charge for this order",
  COUNTER_TITLE: "Pay at Counter",
  STORE_TITLE: "Pay in Store",
  KITCHEN_MESSAGE:
    "Your order will be sent to the kitchen and will be ready at the specified time",
  COUNTER_INSTRUCTION:
    "Please proceed to the counter to pay when your order is ready",
  STORE_INSTRUCTION:
    "Please arrive at the store at the specified time to pay for your order and collect it",
  NO_CHARGE_MESSAGE:
    "There is no charge for this order, please confirm that you wish to continue.",
} as const;

export const BANNER_MESSAGES = {
  STORE_CLOSED: "Store Currently Closed",
  MENU_UNAVAILABLE: "Current Menu Unavailable",
  ONLINE_ORDERING_UNAVAILABLE: "Online Ordering Currently Unavailable",
  TABLE_ORDERING_UNAVAILABLE: "Table Ordering Currently Unavailable",
  OPENS: "Opens",
  AVAILABLE: "Available",
  SERVICE_RESUMES: "Service will resume",
  CLOSED: "Closed",
} as const;

export const FORM_FIELDS = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  PHONE: "phone",
  EMAIL: "email",
  BIRTHDAY: "birthday",
  CODE: "code",
} as const;

export const PLACEHOLDERS = {
  FIRST_NAME: "First Name",
  LAST_NAME: "Last Name",
  PHONE_NUMBER: "Phone Number",
  EMAIL_ADDRESS: "Email Address",
  BIRTHDAY: "dd/mm/yyyy",
  SELECT_TABLE: "Select table",
  CODE: "SMS code from como",
} as const;

export const LABELS = {
  REQUIRED_FOR_YOUR_ORDER: "Required for your order",
  PERSONAL_DETAILS: "Details for your order",
  LOGIN_OR_GUEST: "Log in or guest checkout",
  LOGIN: "Log in",
  SIGNUP: "Sign up for an account",
  REQUEST_CODE: "Request a verification code",
  ORDER_TYPE: "Choose an order type",
  PAYMENT_METHOD: "Payment Method",
  ENTER_CARD: "Enter card number",
  EXPIRY: "Expiry",
  CVC: "CVC",
  BACK_TO_MENU: "Back to menu",
  PROCESSING: "Processing...",
  PAY: "Pay",
  CONFIRM: "Confirm",
  NEXT_STEP: "Next",
  CHANGE: "Change",
  TABLE_NUMBER: "Table number",
  BACK: "Back",
  CANCEL: "Cancel",
  SAVE: "Save",
  CLOSE_DRAWER: "Close drawer",
  ORDER_SENT_HERE: "Order will be sent here",
  WE_SENT_A_CODE: "We have sent you a code to the",
  WILL_BE_SENT: "will be sent to the provided contact information.",
  PLEASE_ENTER_YOUR_PHONE: "Please enter your phone number. A",
  PLEASE_ENTER_YOUR_EMAIL: "Please enter your email. A",
  DIGIT_CODE: "-digit security code",
  SIGN_UP_REQUIRED_FOR: "Signup required for",
  ADD_TO_CART: "Add to cart",
  MY_ACCOUNT: "My Account",
} as const;

export const SUCCESS_SCREEN = {
  TITLE: "Order Successful!",
  FAILED_TITLE: "Order Failed",
  STORE_BUSY_TITLE: "Store is busy",
  ORDER_PLACED: "Your order has been placed successfully.",
  ORDER_NUMBER: "Order number:",
  THANK_YOU: "Thank you for your purchase!",
  OK_BUTTON: "OK",
  RETRY_BUTTON: "Try again",
  FAILED_MESSAGE:
    "We couldn't process your payment. Please try again or use a different payment method.",
  BUSY_MESSAGE:
    "Due to current store conditions, we are unable to process your order. Please try again in a few minutes.",
} as const;

export const PAYMENT_STATUS = {
  SUCCESS: "succeeded",
  FAILED: "failed",
} as const;

export type PaymentStatus =
  (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

export const PAYMENT_PROVIDER = {
  WINDCAVE: {
    CONTAINER_ID: "windcave-container",
    SUPPORTED_CARDS: ["visa", "mastercard", "amex"] as const,
    SIDE_ICONS: ["visa", "mastercard", "amex"] as const,
    STATUS: {
      DONE: "done",
    },
  },
  STRIPE: {
    REDIRECT_STATUS: {
      SUCCEEDED: PAYMENT_STATUS.SUCCESS,
    },
  },
} as const;

export const ERROR_MESSAGES = {
  WINDCAVE: {
    CONTAINER_MISSING: "Couldn't find windcave container",
    LINKS_MISSING:
      "Can't create windcave dropin as no links have been received from the api",
    LIB_FUNCTIONS_MISSING:
      "Windcave library loaded without error but is missing required functions",
    LIB_LOAD_FAILED: "Windcave library failed to load",
    FAILED_TO_CREATE: "Failed to create Windcave payment form",
    PAYMENT_FAILED: "Windcave payment failed",
  },
  STRIPE: {
    NOT_INITIALIZED: "Stripe not initialized",
    VALIDATION_FAILED: "Payment validation failed",
    INITIALIZATION_FAILED: "Payment initialization failed",
    PAYMENT_FAILED: "Payment failed",
  },
  PAYMENT: {
    INVALID_METHOD: "Invalid payment method",
    ORDER_TOTAL_REQUIRED: "Order total is required",
    MISSING_SITE_INFO: "Missing required site information",
    INVALID_STATE: "Invalid payment state",
    ORDER_NUMBER_REQUIRED: "Order number is required for payment",
    ESTIMATE_SURCHARGE_FAILED: "Failed to estimate surcharge",
    GENERIC_PAYMENT_FAILED: "Payment failed",
  },
} as const;

enum ErrorType {
  MODIFIER_LIMIT_REACHED = "MODIFIER_LIMIT_REACHED",
  ITEM_LIMIT_REACHED = "ITEM_LIMIT_REACHED",
  SERVICE_NOT_AVAILABLE = "SERVICE_NOT_AVAILABLE",
  ASAP_NOT_ALLOWED = "ASAP_NOT_ALLOWED",
  REQUESTED_TIME_TOO_FAR_AHEAD = "REQUESTED_TIME_TOO_FAR_AHEAD",
  PRODUCT_SOLD_OUT = "PRODUCT_SOLD_OUT",
}

export function isErrorType(value: string): value is ErrorType {
  return Object.values(ErrorType).includes(value as ErrorType);
}

export const HANDLED_ERROR: Record<
  ErrorType,
  {
    icon: () => JSX.Element;
    title: string;
    message: string;
  }
> = {
  MODIFIER_LIMIT_REACHED: {
    icon: ShoppingCartIcon,
    title: "Too many modifiers",
    message: "Too many modifiers selected, please remove some.",
  },
  ITEM_LIMIT_REACHED: {
    icon: ShoppingCartIcon,
    title: "Too many items",
    message: "Too many items in cart, please remove some.",
  },
  SERVICE_NOT_AVAILABLE: {
    icon: FailedIcon,
    title: "Service not available",
    message: "Order time is no longer valid.",
  },
  ASAP_NOT_ALLOWED: {
    icon: FailedIcon,
    title: "ASAP ordering not available",
    message: "Order time is no longer valid.",
  },
  REQUESTED_TIME_TOO_FAR_AHEAD: {
    icon: FailedIcon,
    title: "Requested time not available",
    message: "Order time is no longer valid.",
  },
  PRODUCT_SOLD_OUT: {
    icon: SoldOutIcon,
    title: "Sold out",
    message: "One or more products in your cart have been sold out.",
  },
};

export const KEYS = {
  SUBMIT_ORDER_FETCHER: "submit-order-fetcher",
  MY_ACCOUNT_FETCHER: "my-account-fetcher",
  DELETE_CARD_FETCHER: "delete-card-fetcher",
  LAST_RESET: "last-reset",
};

export const COMO_REDEMPTION_COOLDOWN = 70_000;
